import Guard from 'arg-guard'
import MathTools from '@/helpers/math-tools'

/**
 * Defined structure of a service basket item.
 * @export ServiceBasketItem
 * @class ServiceBasketItem
 */
export default class ServiceBasketItem {

    /**
     * Creates an instance of a ServiceBasketItem.
     * @param {number|ServiceType} serviceType The ServiceType or it's identifier.
     * @param {number} addressId The address identifier.
     * @param {?number} cost The cost - If not provided will be stored as 0 / Free
     * @param {?string} name Required if the ServiceType is provided via an identifier.
     * @param {?Object} data Optional extra data.
     * @memberof ServiceBasketItem
     */
    constructor(serviceType, addressId, cost, name = null, data = null) {
        Guard.throwIf(addressId, 'addressId')

        if (typeof serviceType === 'number') {
            Guard.throwIf(name, 'name')
            this.serviceTypeId = serviceType

            /**
             * @type {string}
             */
            this.name = name
        } else {
            this.serviceTypeId = serviceType.id
            this.name = serviceType.label
        }

        /**
         * @type {number}
         */
        this.addressId = addressId

        /**
         * @type {number}
         */
        this.cost = cost || 0

        if (data) {
            /**
             * @type {Object}
             */
            this.data = data
        }
    }

    /**
     * @type {string}
     */
    get costDisplay() {
        return this.cost > 0 ? MathTools.segmentCurrency(this.cost) : 'Free'
    }

    /**
     * Returns the object in a form suitable to store.
     * @returns {ServiceBasketItemAsJson}
     * @memberof ServiceBasketItem
     */
    toStoreJson() {
        return {
            serviceTypeId: this.serviceTypeId,
            addressId: this.addressId,
            cost: this.cost,
            name: this.name,
            data: this.data || null,
        }
    }

    /**
     * Ensures that the provided data is already of this type. If not, it runs the constructor.
     * @param {ServiceBasketItem|ServiceBasketItemAsJson|ServiceBasketItemSourceData} data Source data - From API or state.
     * @returns {ServiceBasketItem} Either the data is passed back if already of this type or the result of the constructor is.
     * @memberof ServiceBasketItem
     */
    static ensure(data) {
        return data && data instanceof ServiceBasketItem
            ? data
            : new ServiceBasketItem(data.serviceTypeId, data.addressId, data.cost,
                data.name, data.data)
    }

    /**
     * Returns the object type from raw stored JSON data.
     * @param {?ServiceBasketItemAsJson} data The stored data.
     * @memberof ServiceBasketItem
     */
    static fromStoreJson(data = null) {
        return data ? ServiceBasketItem.ensure(data) : null
    }
}
